import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { FeathersService } from '../../feathers.service'
import { map, shareReplay, tap } from 'rxjs/operators'
import { AssistantAvailability } from '../../../models/assistantAvailability'
import { ToasterService } from '../../../features/notification/toaster.service'
import { RecurrenceService } from '../recurrence/recurrence.service'
import { DateRange, DateUtils } from '../../date-utils.service'
import { RecurrenceEditingMode } from '../../../features/recurrence/recurrenceEditing'
import { subDays } from 'date-fns'
import {
  AssistantAvailabilityListParams,
  AssistantsCalendarParams,
} from 'src/app/pages/assistant/assistant-availabilities/assistant-availabilities.page'

import { ListAssistantAvailability } from 'src/app/models/listAssistantAvailability'
import { CalendarAssistantAvailability } from '../../../models/calendarAssistantAvailability'
import { CalendarAssistant } from '../../../models/calendarAssistant'

export type AssistantAvailabilityQueryType = 'full' | 'calendar' | 'list' | 'roleAssistantCalendar'

@Injectable({
  providedIn: 'root',
})
export class AssistantAvailabilitiesService extends RecurrenceService {
  allStream$: Observable<AssistantAvailability[]>
  TAG: string = 'AssistantAvailabilitiesService'

  constructor(
    private feathers: FeathersService,
    public toaster: ToasterService,
  ) {
    super(toaster)
  }

  private init() {
    this.allStream$ = this.feathers.find(AssistantAvailability, {}).pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAllWithCaching(): Observable<AssistantAvailability[]> {
    if (!this.allStream$) this.init()

    return this.allStream$.pipe(tap((e) => console.log('AssAvail getAllwithCaching', e)))
  }

  queryFeathers(query: {}): Observable<AssistantAvailability[]> {
    return this.feathers.find(AssistantAvailability, query)
  }

  queryFeathersPromise(query: {}): Promise<AssistantAvailability[]> {
    return this.feathers.getPromise(AssistantAvailability, query)
  }

  create(aa: AssistantAvailability, propsToCreateFrom: any): Promise<AssistantAvailability> {
    return this.feathers.createWithFactory(AssistantAvailability, aa.updateFromProps(propsToCreateFrom))
  }

  updateNonRecurrently(aa: AssistantAvailability): Promise<AssistantAvailability> {
    console.log('updateNonRecurrently, rrule:', aa.rrule)
    return this.feathers.upsertWithFactory(AssistantAvailability, aa)
  }

  isOverlappingAnyAvailabilityForTheSameAssistant(
    dateFrom: Date,
    dateTo: Date,
    assistantId: number,
    currentAvailabilityId: number,
    oldAvailability: AssistantAvailability,
    rrule: string,
  ): Observable<any[]> {
    return this.feathers.findOnGenericServiceWithArrayRecordStore('assistant-availabilities-overlaps', {
      dateFrom,
      dateTo,
      assistantId,
      oldAssistantAvailabilityDateFrom: oldAvailability.dt_start,
      oldAssistantAvailabilityDateTo: oldAvailability.dt_end,
      currentAvailabilityId,
      oldAvailabilityRecurrenceParentId: oldAvailability.recurrenceParentId,
      rrule,
    })
  }

  async remove(assistantAvailability: AssistantAvailability) {
    console.log(this.TAG, 'remove', assistantAvailability)
    if (assistantAvailability.isRecurrentButNotException()) {
      const newSeries = await this.update(assistantAvailability, RecurrenceEditingMode.currentOccurrenceAndFollowing)
      return this.feathers.remove(AssistantAvailability.serviceName, newSeries)
    } else {
      return this.feathers.remove(AssistantAvailability.serviceName, assistantAvailability)
    }
  }

  getAvailabilitiesByAssistantId(assistantId: number, query: any = {}): Observable<AssistantAvailability[]> {
    console.log('getAvailabilitiesByAssistantId', assistantId)
    query.assistantId = assistantId
    return this.feathers.find(AssistantAvailability, query)
  }

  async createNewAvailability(props?: Partial<AssistantAvailability>) {
    return AssistantAvailability.createFromProps(AssistantAvailability, props)
  }

  /**
   * Gets all Avails that fall into a given date range, recurring and non-recurring.
   *
   * Query explanation:
   * There can be either a one-off Assistance or a recurring one.
   * With a recurring avail, we cannot select an ocurrence directly from the database as they are zipped up inside an Rrule.
   * So in case of a recurring assistance, we have to find the parent record, ie. the one that is first in the recurrence series.
   *
   * If the parent Avail falls inside the given date range, it will be selected the same way as the one-off Avail.
   * If the parent Avail only has occurrences (not the parent record itself) inside the given range, its parent record will start earlier than the range start, it will have an rrule field set and it may or may not have a recurrenceEnd field set (infinite vs. finite recurrence).
   *
   * The result is the most precise we can get from the DB and is intended to have its RRULEs expanded client-side and further filtered
   *
   */
  getByPlannedDateRange(
    range: DateRange,
    queryType: AssistantAvailabilityQueryType = 'full',
  ): Observable<AssistantAvailability[]> {
    const query = {
      queryType,
      $or: [
        {
          dt_start: { $lte: range.to },
          dt_end: { $gte: range.from },
          rrule: null,
        },

        {
          dt_start: {
            $lte: range.to,
            $gte: DateUtils.subtractDaysFromDate(range.from, 365),
          },
          recurrenceEnd: {
            $or: [{ $gte: subDays(range.from, 1) }, null],
          },
          rrule: { $ne: null },
        },
      ],
      // $limit: 3000
    }

    return this.queryFeathers(query)
  }

  /**
   * Retrieves a list of assistants for the calendar view, sorted by last name
   * @param params Filter parameters for calendar view including date range and contract types
   * @returns Observable of AssistantCalendar array sorted by last name
   */
  getCalendarAssistants(params: AssistantsCalendarParams): Observable<CalendarAssistant[]> {
    console.log('getCalendarAssistants', params)
    return this.feathers
      .find(CalendarAssistant, params)
      .pipe(map((assistants) => assistants.sort((a, b) => a.lastName.localeCompare(b.lastName))))
  }

  /**
   * Retrieves availability data for assistants in calendar view
   * @param params Filter parameters for calendar view including date range
   * @returns Observable of AssistantCalendarAvailability array
   */
  getCalendarAssistantAvailabilities(params: AssistantsCalendarParams): Observable<CalendarAssistantAvailability[]> {
    console.log('getCalendarAssistantAvailabilities', params)
    return this.feathers.find(CalendarAssistantAvailability, params)
  }

  /**
   * Retrieves a list of assistant availabilities for the list view, sorted by assistant last name
   * @param params Filter parameters for list view including date range, workgroup, and contract types
   * @returns Observable of AssistantListAvailability array sorted by assistant last name
   */
  getListAssistantAvailabilities(params: AssistantAvailabilityListParams): Observable<ListAssistantAvailability[]> {
    return this.feathers.find(ListAssistantAvailability, params).pipe(
      map((unexpandedAvailabilities) =>
        ListAssistantAvailability.expandAvailabilitiesByRrules(unexpandedAvailabilities, {
          from: new Date(params.dateRangeFrom),
          to: new Date(params.dateRangeTo),
        }),
      ),
      map((availabilities) => availabilities.sort((a, b) => a.assistantLastName.localeCompare(b.assistantLastName))),
    )
  }

  getById(id: number) {
    return this.queryFeathers({ id })
  }
}
